import { DEFAULT_LOCALE, type Locale } from "~/i18n/locales";
import { HELP_CENTER_LINKS } from "@constants/externalLinks";

/**
 * Region-specific help-center URLs. Like the legal links, the locale is derived
 * from the refund currency (see `localeForCurrency`) — never the browser — so
 * an AU refund points at the `en-au` help center and a US refund at `en-us`,
 * regardless of the customer's device settings.
 *
 * The US help center hosts a deep-linked article per topic. The AU help center
 * has no published article deep-links yet, so every AU topic points at the AU
 * landing page (`/hc/en-au`) rather than 404-ing on a US article id. As real AU
 * articles ship, swap the relevant lines in `AU_HELP_CENTER_LINKS` one by one.
 */
export type HelpCenterLinks = typeof HELP_CENTER_LINKS;

/** AU help-center landing page — the default until AU deep-links are published. */
const AU_HELP_CENTER = "https://help.reshop.com/hc/en-au";

/**
 * AU help-center links. Every topic defaults to the AU landing page for now;
 * replace an individual value with its published AU article URL as they land.
 */
const AU_HELP_CENTER_LINKS: HelpCenterLinks = {
  default: AU_HELP_CENTER,
  login: {
    login: AU_HELP_CENTER,
  },
  onboarding: {
    mobileVerification: AU_HELP_CENTER,
    profileConfirmation: AU_HELP_CENTER,
    cardConnection: AU_HELP_CENTER,
  },
  externalConnections: {
    bankAccountError: AU_HELP_CENTER,
    sendRefundToCard: AU_HELP_CENTER,
  },
  errors: {
    accountBlocked: AU_HELP_CENTER,
    refundDeclined: AU_HELP_CENTER,
    refundExpired: AU_HELP_CENTER,
  },
};

const HELP_CENTER_LINKS_BY_LOCALE = new Map<Locale, HelpCenterLinks>([
  ["en-US", HELP_CENTER_LINKS],
  ["en-AU", AU_HELP_CENTER_LINKS],
]);

/**
 * Resolves the help-center URLs for a locale, falling back to the US deep-links
 * for any locale without a published regional help center.
 *
 * @param locale - The resolved {@link Locale}, e.g. from `useLocale()` or `localeForCurrency(currency)`
 * @returns The region-appropriate help-center links
 * @example getHelpCenterLinks("en-US").errors.refundDeclined // US article deep-link
 * @example getHelpCenterLinks("en-AU").errors.refundDeclined // "https://help.reshop.com/hc/en-au"
 */
export function getHelpCenterLinks(locale: Locale): HelpCenterLinks {
  return (
    HELP_CENTER_LINKS_BY_LOCALE.get(locale) ??
    HELP_CENTER_LINKS_BY_LOCALE.get(DEFAULT_LOCALE) ??
    HELP_CENTER_LINKS
  );
}
